import type {
  VerticalDatumData,
  AbsoluteVerticalDatumData,
  RelativeVerticalDatumData,
  RelativeDatumTable,
  RelativeDatumRenamerTable,
} from "building-types/columns/columnDatumTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import { RelativeVerticalDatum, VerticalDatumType } from "building-types/datums/datumsTypesEnum";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import type { DefaultVerticalDatumDataId } from "doc/elements/columns/columnDefaults";
import { getDatumZ } from "../../elements/datums";

export function isAbsoluteVerticalDatum(datum: VerticalDatumData): datum is AbsoluteVerticalDatumData {
  return datum.type === VerticalDatumType.ABSOLUTE;
}

export function isRelativeVerticalDatum(datum: VerticalDatumData): datum is RelativeVerticalDatumData {
  return datum.type === VerticalDatumType.RELATIVE;
}

export function defaultVerticalDatumId(type: VerticalDatumType): DefaultVerticalDatumDataId {
  switch (type) {
    case VerticalDatumType.RELATIVE: {
      return "defaultRelativeVerticalDatumData";
    }
    case VerticalDatumType.ABSOLUTE: {
      return "defaultAbsoluteVerticalDatumData";
    }
    default:
      assertUnreachable(type);
  }
}

export const relativeDatumRenamerTable: RelativeDatumRenamerTable = {
  [RelativeVerticalDatum.TOP_OF_FF_HOST]: "T.O. F.F. - Current Host",
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_HOST]: "T.O. Wall Plate - Current Host",
  [RelativeVerticalDatum.TOP_OF_FF_ABOVE_HOST]: "T.O. F.F. - Above Host",
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_ABOVE_HOST]: "T.O. Wall Plate - Above Host",
  [RelativeVerticalDatum.TOP_OF_FF_FINISH_BELOW_HOST]: "T.O. F.F. Finish - Below Host",
  [RelativeVerticalDatum.TOP_OF_WALL_PLATE_BELOW_HOST]: "T.O. Wall Plate - Below Host",
  [RelativeVerticalDatum.STAIR_SYSTEM]: "Stair System - Current Host",
  [RelativeVerticalDatum.BOTTOM_OF_STAIR_SYSTEM_HOST]: "B.O. Stair System - Current Host",
};

export function calculateVerticalDatumZ(
  id: keyof Pick<VerticalDatumData, "topDatumId" | "bottomDatumId">,
  datum: VerticalDatumData,
  resolvedDatums?: ResolvedDatumTable,
  relativeDatums?: RelativeDatumTable,
): WorldInches {
  let offset: WorldInches;
  switch (id) {
    case "topDatumId": {
      offset = datum.topOffset ? worldInchesNew(datum.topOffset) : worldInchesNew(0);
      break;
    }
    case "bottomDatumId": {
      offset = datum.bottomOffset ? worldInchesNew(datum.bottomOffset) : worldInchesNew(0);
      break;
    }
    default:
      assertUnreachable(id);
  }

  switch (datum.type) {
    case VerticalDatumType.RELATIVE: {
      const datumId = datum[id];
      if (datumId === undefined) {
        return offset;
      }
      return (relativeDatums?.[datumId] ?? 0) + offset;
    }
    case VerticalDatumType.ABSOLUTE: {
      const datumId = datum[id];
      if (datumId === undefined) {
        return worldInchesNew(offset);
      }
      const resolvedDatum = resolvedDatums?.[datumId];
      if (resolvedDatum === undefined) {
        return worldInchesNew(offset);
      }
      return worldInchesNew(getDatumZ(resolvedDatum) + offset);
    }
    default:
      assertUnreachable(datum);
  }
}
